import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Checkbox,
  Form,
  Input,
  Select,
  Space,
  Typography,
  message,
} from "antd";
import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import { Editor } from "@tinymce/tinymce-react";
import { useDispatch } from "react-redux";
import {
  createForm,
  updateForm,
  testFormNotification,
} from "../../../redux/actions/formsAction";
import { useFormDetail } from "./FormDetailContext";
import { formDetailPath } from "./formUtils";

const { Title } = Typography;

// Mirrors the Quick Campaign editor's TinyMCE config, sized down for a
// settings-page field rather than a full-height drawer.
function TinyMceHtmlEditor({ value, onChange }) {
  return (
    <Editor
      tinymceScriptSrc={"/tinymce/tinymce.min.js"}
      value={value || ""}
      onEditorChange={(content) => onChange?.(content)}
      init={{
        skin: "oxide",
        content_css: "default",
        branding: false,
        height: 320,
        menubar: false,
        plugins: [
          "advlist",
          "autolink",
          "lists",
          "link",
          "image",
          "charmap",
          "preview",
          "insertdatetime",
          "anchor",
          "searchreplace",
          "visualblocks",
          "code",
          "media",
          "table",
          "wordcount",
          "quickbars",
          "emoticons",
          "fullscreen",
          "importcss",
          "directionality",
          "visualchars",
          "codesample",
          "pagebreak",
          "nonbreaking",
          "help",
        ],
        toolbar: [
          "undo redo | fontfamily fontsize | bold italic underline | forecolor backcolor | alignleft aligncenter alignright alignjustify | bullist numlist | outdent indent | removeformat",
          "link emoticons charmap image media table codesample | code preview fullscreen",
        ],
      }}
    />
  );
}

const VARIABLES_HELP =
  "Use the same field names as in your embed form, wrapped in double braces. Examples: {{email}}, {{firstName}}, {{lastName}}, {{phoneNumber}}, {{formName}}";

const DEFAULT_NOTIFY_HTML = `<p>You have a new submission on {{formName}}.</p>
<p><strong>Email:</strong> {{email}}<br/>
<strong>First name:</strong> {{firstName}}<br/>
<strong>Last name:</strong> {{lastName}}<br/>
<strong>Phone:</strong> {{phoneNumber}}</p>`;

const DEFAULT_AUTO_REPLY_HTML = `<p>Thank you for contacting us. We have received your submission.</p>
<p><strong>Your email:</strong> {{email}}<br/>
<strong>Name:</strong> {{firstName}} {{lastName}}<br/>
<strong>Phone:</strong> {{phoneNumber}}</p>`;

function normalizeOriginInput(value) {
  const s = String(value || "").trim();
  if (!s) return null;
  if (s === "*") return "*";
  try {
    const href = s.includes("://") ? s : `https://${s}`;
    return new URL(href).origin;
  } catch {
    return null;
  }
}

function normalizeOriginsList(list) {
  if (!Array.isArray(list)) return [];
  const out = [];
  for (const item of list) {
    const o = normalizeOriginInput(item);
    if (o && !out.includes(o)) out.push(o);
  }
  return out;
}

function getLoggedInUserEmail() {
  try {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    return user.emailAddress || "";
  } catch {
    return "";
  }
}

export default function FormSettings() {
  const { id, isNew, formRecord, reloadForm } = useFormDetail();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [testSending, setTestSending] = useState(false);

  const contactPromotion = Form.useWatch("contactPromotion", form);
  const notifyOwner = Form.useWatch("notifyOwner", form);
  const autoResponderEnabled = Form.useWatch("autoResponderEnabled", form);
  const recaptchaEnabled = Form.useWatch("recaptchaEnabled", form);
  const preventDuplicatesEnabled = Form.useWatch(
    "preventDuplicatesEnabled",
    form,
  );
  const integrationType = Form.useWatch("integrationType", form);

  useEffect(() => {
    if (isNew) {
      const userEmail = getLoggedInUserEmail();
      form.setFieldsValue({
        enabled: true,
        contactPromotion: "manual",
        // FR-3.12: double opt-in by default on every new form -- this
        // codebase captures no org billing country to scope the default
        // to India specifically, and the PRD's own explicit fallback for
        // that case is to default every org. Still just the pre-filled
        // value; the toggle right below stays fully editable.
        consentMode: "double",
        notifyOwner: true,
        ownerEmails: userEmail,
        notifySubject: "New submission: {{formName}}",
        notifyHtml: DEFAULT_NOTIFY_HTML,
        autoResponderEnabled: false,
        autoResponderSubject: "New submission: {{formName}}",
        autoResponderHtml: DEFAULT_AUTO_REPLY_HTML,
        integrationType: "none",
        recaptchaEnabled: false,
        preventDuplicatesEnabled: false,
        preventDuplicatesMessage: "",
        requireNotSpam: true,
        requireConfirmed: true,
        requireCaptcha: false,
        antiSpamMode: "",
      });
      return;
    }

    if (formRecord) {
      const settings = formRecord.settings || {};
      const integration =
        settings.integrationType === "generic" ||
        settings.integrationType === "slack"
          ? settings.integrationType
          : settings.webhookUrl
            ? settings.integrationType || "generic"
            : "none";

      form.setFieldsValue({
        name: formRecord.name,
        enabled: formRecord.enabled,
        defaultTags: formRecord.defaultTags?.join(", "),
        contactPromotion: formRecord.contactPromotion,
        consentMode: formRecord.consentMode,
        redirectUrl: settings.redirectUrl,
        notifyOwner: settings.notifyOwner !== false,
        ownerEmails:
          settings.ownerEmails?.length > 0
            ? settings.ownerEmails.join(", ")
            : getLoggedInUserEmail(),
        notifySubject: settings.notifySubject || "New submission: {{formName}}",
        notifyHtml: settings.notifyHtml || DEFAULT_NOTIFY_HTML,
        autoResponderEnabled: settings.autoResponder?.enabled,
        autoResponderSubject:
          settings.autoResponder?.subject || "New submission: {{formName}}",
        autoResponderHtml:
          settings.autoResponder?.html || DEFAULT_AUTO_REPLY_HTML,
        webhookUrl: settings.webhookUrl,
        webhookSecret: settings.webhookSecret,
        integrationType: integration,
        recaptchaEnabled: settings.recaptcha?.enabled,
        recaptchaSiteKey: settings.recaptcha?.siteKey,
        preventDuplicatesEnabled: settings.preventDuplicates?.enabled,
        preventDuplicatesMessage: settings.preventDuplicates?.message || "",
        requireConfirmed: formRecord.workflowRules?.requireConfirmed,
        requireNotSpam: formRecord.workflowRules?.requireNotSpam,
        requireCaptcha: formRecord.workflowRules?.requireCaptcha,
        allowedOrigins: settings.allowedOrigins || [],
        antiSpamMode: settings.antiSpam?.mode || "",
      });
    }
  }, [formRecord, form, isNew]);

  const buildPayload = (values) => {
    const integration = values.integrationType || "none";
    const webhookEnabled = integration !== "none";

    return {
      name: values.name,
      enabled: values.enabled !== false,
      defaultTags: values.defaultTags
        ? values.defaultTags
            .split(",")
            .map((t) => t.trim())
            .filter(Boolean)
        : ["form-lead"],
      contactPromotion: values.contactPromotion || "manual",
      consentMode: values.consentMode || "double",
      workflowRules: {
        requireConfirmed: !!values.requireConfirmed,
        requireNotSpam: values.requireNotSpam !== false,
        requireCaptcha: !!values.requireCaptcha,
      },
      settings: {
        redirectUrl: values.redirectUrl || "",
        notifyOwner: values.notifyOwner !== false,
        ownerEmails: values.ownerEmails
          ? values.ownerEmails
              .split(",")
              .map((e) => e.trim())
              .filter(Boolean)
          : [],
        notifySubject: values.notifySubject,
        notifyHtml: values.notifyHtml,
        autoResponder: {
          enabled: !!values.autoResponderEnabled,
          subject: values.autoResponderSubject,
          html: values.autoResponderHtml,
        },
        webhookUrl: webhookEnabled ? values.webhookUrl || null : null,
        webhookSecret: webhookEnabled ? values.webhookSecret || null : null,
        integrationType: integration,
        recaptcha: {
          enabled: !!values.recaptchaEnabled,
          siteKey: values.recaptchaSiteKey,
        },
        preventDuplicates: {
          enabled: !!values.preventDuplicatesEnabled,
          message: values.preventDuplicatesMessage?.trim() || "",
        },
        allowedOrigins: normalizeOriginsList(values.allowedOrigins),
        antiSpam: values.antiSpamMode
          ? { mode: values.antiSpamMode }
          : { mode: null },
      },
    };
  };

  const buildNotificationTestPayload = (values) => ({
    notifyOwner: values.notifyOwner !== false,
    ownerEmails: values.ownerEmails
      ? values.ownerEmails
          .split(",")
          .map((e) => e.trim())
          .filter(Boolean)
      : [],
    notifySubject: values.notifySubject,
    notifyHtml: values.notifyHtml,
  });

  const handleTestNotification = async () => {
    if (isNew) {
      message.warning("Save the form first, then send a test notification.");
      return;
    }
    try {
      const values = form.getFieldsValue(true);
      if (!values.name?.trim()) {
        message.warning("Enter a form name first.");
        return;
      }
      setTestSending(true);
      await dispatch(updateForm(buildPayload(values), id));
      await dispatch(
        testFormNotification(id, buildNotificationTestPayload(values)),
      );
      message.success("Test notification sent. Check your inbox.");
      reloadForm();
    } catch (err) {
      message.error(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to send test notification",
      );
    } finally {
      setTestSending(false);
    }
  };

  const onFinish = (values) => {
    setLoading(true);
    const payload = buildPayload(values);
    const action = isNew ? createForm(payload) : updateForm(payload, id);
    dispatch(action)
      .then((result) => {
        if (isNew && !result?._id) {
          message.error("Form created but no id returned.");
          setLoading(false);
          return;
        }
        message.success(isNew ? "Form created" : "Settings saved");
        if (isNew) {
          navigate(formDetailPath(result._id, "overview"), { replace: true });
        } else {
          reloadForm();
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={onFinish}
      initialValues={{
        enabled: true,
        contactPromotion: "manual",
        consentMode: "double",
        notifyOwner: true,
        integrationType: "none",
        requireNotSpam: true,
        requireConfirmed: true,
        allowedOrigins: [],
        antiSpamMode: "",
      }}
    >
      <Form.Item name="name" label="Form name" rules={[{ required: true }]}>
        <Input />
      </Form.Item>
      <Form.Item name="defaultTags" label="Tags (when promoted to contacts)">
        <Input placeholder="website-lead, newsletter" />
      </Form.Item>
      <Form.Item
        name="consentMode"
        label="Consent mode"
        help="Recommended for DPDP evidence, not legally required"
      >
        <Select
          options={[
            { value: "single", label: "Single opt-in" },
            { value: "double", label: "Double opt-in (email confirmation)" },
          ]}
        />
      </Form.Item>
      <Form.Item name="contactPromotion" label="Contact promotion">
        <Select
          options={[
            {
              value: "manual",
              label: "Manual — review submission, then add to contacts",
            },
            {
              value: "workflow",
              label: "Workflow — auto-promote when rules pass",
            },
          ]}
        />
      </Form.Item>

      {contactPromotion === "workflow" && (
        <>
          <Form.Item name="requireConfirmed" valuePropName="checked">
            <Checkbox>Require email confirmed (double opt-in)</Checkbox>
          </Form.Item>
          <Form.Item name="requireNotSpam" valuePropName="checked">
            <Checkbox>Require not spam/rejected</Checkbox>
          </Form.Item>
          <Form.Item name="requireCaptcha" valuePropName="checked">
            <Checkbox>Require CAPTCHA passed</Checkbox>
          </Form.Item>
        </>
      )}

      <Title level={5} style={{ marginTop: 24 }}>
        Anti-spam
      </Title>
      <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
        Per-form override. When set to server default, this form uses{" "}
        <code>ANTI_SPAM_MODE</code> from the API environment (<em>shadow</em>,{" "}
        <em>enforce</em>, or <em>off</em>).
      </Typography.Paragraph>
      <Form.Item
        name="antiSpamMode"
        label="Anti-spam mode"
        extra="Shadow scores submissions but does not block. Enforce marks high-score submissions as spam and skips notifications."
      >
        <Select
          options={[
            { value: "", label: "Server default (from environment)" },
            { value: "shadow", label: "Shadow — score only" },
            { value: "enforce", label: "Enforce — block spam submissions" },
            { value: "off", label: "Off — disable anti-spam" },
          ]}
        />
      </Form.Item>

      <Form.Item name="redirectUrl" label="Thank-you redirect URL">
        <Input placeholder="https://yoursite.com/thanks" />
      </Form.Item>

      <Title level={5} style={{ marginTop: 24 }}>
        Allowed origins
      </Title>
      <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
        Optional. When set, browser submissions must come from one of these
        origins (the <code>Origin</code> header). Use full URLs such as{" "}
        <code>https://www.example.com</code>, or <code>*</code> to allow any.
        Leave empty to allow all origins. Server-side posts (PHP, curl) are not
        blocked by this list.
      </Typography.Paragraph>
      <Form.List name="allowedOrigins">
        {(fields, { add, remove }) => (
          <>
            {fields.map((field) => (
              <Space
                key={field.key}
                style={{ display: "flex", marginBottom: 8 }}
                align="baseline"
              >
                <Form.Item
                  {...field}
                  style={{ flex: 1, marginBottom: 0 }}
                  rules={[
                    {
                      validator: async (_, value) => {
                        const s = String(value || "").trim();
                        if (!s) return Promise.resolve();
                        if (s === "*") return Promise.resolve();
                        if (normalizeOriginInput(s)) return Promise.resolve();
                        return Promise.reject(
                          new Error(
                            "Enter a valid URL (e.g. https://www.example.com) or *",
                          ),
                        );
                      },
                    },
                  ]}
                >
                  <Input placeholder="https://www.example.com" />
                </Form.Item>
                <MinusCircleOutlined onClick={() => remove(field.name)} />
              </Space>
            ))}
            <Button
              type="dashed"
              onClick={() => add("")}
              block
              icon={<PlusOutlined />}
            >
              Add origin
            </Button>
          </>
        )}
      </Form.List>

      <Title level={5}>Owner notifications</Title>
      <Form.Item name="notifyOwner" valuePropName="checked">
        <Checkbox
          onChange={(e) => {
            if (e.target.checked) {
              const patch = {};
              if (!form.getFieldValue("ownerEmails")) {
                patch.ownerEmails = getLoggedInUserEmail();
              }
              if (!form.getFieldValue("notifySubject")) {
                patch.notifySubject = "New submission: {{formName}}";
              }
              if (!form.getFieldValue("notifyHtml")) {
                patch.notifyHtml = DEFAULT_NOTIFY_HTML;
              }
              if (Object.keys(patch).length) form.setFieldsValue(patch);
            }
          }}
        >
          Email me on new submissions
        </Checkbox>
      </Form.Item>

      {notifyOwner && (
        <>
          <Form.Item
            name="ownerEmails"
            label="Notification emails (comma-separated)"
          >
            <Input placeholder={getLoggedInUserEmail()} />
          </Form.Item>
          <Form.Item name="notifySubject" label="Notification subject">
            <Input placeholder="New submission: {{formName}}" />
          </Form.Item>
          <Form.Item
            name="notifyHtml"
            label="Notification body (HTML)"
            extra={VARIABLES_HELP}
          >
            <TinyMceHtmlEditor />
          </Form.Item>
          {!isNew && (
            <Button
              loading={testSending}
              onClick={handleTestNotification}
              style={{ marginBottom: 16 }}
            >
              Send test notification
            </Button>
          )}
        </>
      )}

      <Title level={5} style={{ marginTop: 24 }}>
        Auto-responder
      </Title>
      <Form.Item name="autoResponderEnabled" valuePropName="checked">
        <Checkbox
          onChange={(e) => {
            if (e.target.checked) {
              const patch = {};
              if (!form.getFieldValue("autoResponderSubject")) {
                patch.autoResponderSubject = "New submission: {{formName}}";
              }
              if (!form.getFieldValue("autoResponderHtml")) {
                patch.autoResponderHtml = DEFAULT_AUTO_REPLY_HTML;
              }
              if (Object.keys(patch).length) form.setFieldsValue(patch);
            }
          }}
        >
          Send auto-reply to submitter (does not create a contact)
        </Checkbox>
      </Form.Item>

      {autoResponderEnabled && (
        <>
          <Form.Item name="autoResponderSubject" label="Auto-reply subject">
            <Input placeholder="New submission: {{formName}}" />
          </Form.Item>
          <Form.Item
            name="autoResponderHtml"
            label="Auto-reply body (HTML)"
            extra={VARIABLES_HELP}
          >
            <TinyMceHtmlEditor />
          </Form.Item>
        </>
      )}

      <Title level={5} style={{ marginTop: 24 }}>
        Spam protection
      </Title>
      <Form.Item name="recaptchaEnabled" valuePropName="checked">
        <Checkbox>Enable reCAPTCHA on public form</Checkbox>
      </Form.Item>

      {recaptchaEnabled && (
        <Form.Item name="recaptchaSiteKey" label="reCAPTCHA site key">
          <Input />
        </Form.Item>
      )}

      <Title level={5} style={{ marginTop: 24 }}>
        Duplicate submissions
      </Title>
      <Form.Item name="preventDuplicatesEnabled" valuePropName="checked">
        <Checkbox>
          Block duplicate submissions from the same email address
        </Checkbox>
      </Form.Item>
      <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
        When enabled, a second submission with the same email on this form
        returns an already-submitted message instead of creating another entry.
        Rejected or spam submissions do not count.
      </Typography.Paragraph>

      {preventDuplicatesEnabled && (
        <Form.Item
          name="preventDuplicatesMessage"
          label="Already submitted message"
          extra="Leave blank to use the default message."
        >
          <Input placeholder="You have already submitted this form." />
        </Form.Item>
      )}

      <Title level={5} style={{ marginTop: 24 }}>
        Webhooks
      </Title>
      <Form.Item name="integrationType" label="Integration type">
        <Select
          options={[
            { value: "none", label: "None" },
            { value: "generic", label: "Generic JSON webhook" },
            { value: "slack", label: "Slack incoming webhook" },
          ]}
        />
      </Form.Item>

      {integrationType && integrationType !== "none" && (
        <>
          <Form.Item name="webhookUrl" label="Webhook URL">
            <Input />
          </Form.Item>
          <Form.Item name="webhookSecret" label="Webhook signing secret">
            <Input.Password />
          </Form.Item>
        </>
      )}

      <Space>
        <Button type="primary" htmlType="submit" loading={loading}>
          {isNew ? "Create form" : "Save settings"}
        </Button>
        <Button onClick={() => navigate("/app/forms")}>Cancel</Button>
      </Space>
    </Form>
  );
}
